import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../lib/api";
import { cn } from "../../lib/cn";

type CustomerItem = {
  id: string;
  full_name: string;
  phone?: string | null;
  email?: string | null;
  branch_id?: string | null;
};

type Branch = {
  id: string;
  name: string;
};

type Service = {
  id: string;
  name: string;
  duration_minutes: number;
};

type Staff = {
  id: string;
  full_name: string;
  branch_id: string;
  is_active: boolean;
};

type Slot = {
  staff_id: string;
  start_at: string;
  end_at: string;
  score: number;
};

type AppointmentCreateResult = {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
};

type LastAppointmentSummary = {
  appointment_id: string;
  branch_id: string;
  service_id: string;
  service_name?: string | null;
  staff_id?: string | null;
  staff_name?: string | null;
  status: string;
  start_at: string;
  end_at: string;
};

function isoDate(d: Date) {
  return d.toISOString().split("T")[0];
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function buildDateStrip(anchor: Date): Date[] {
  const days: Date[] = [];
  for (let i = -3; i <= 3; i += 1) {
    const d = new Date(anchor);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

function StepIndicator({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-1.5 rounded-full transition-all",
            i + 1 === step ? "w-7 bg-emerald-600" : i + 1 < step ? "w-3 bg-emerald-300" : "w-3 bg-slate-200"
          )}
        />
      ))}
    </div>
  );
}

export function ManagerBookPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();

  const customerIdFromParam = searchParams.get("customer_id") ?? null;
  const customerNameFromParam = searchParams.get("customer_name") ?? "";
  const branchIdFromParam = searchParams.get("branch_id") ?? null;
  const serviceIdFromParam = searchParams.get("service_id") ?? null;
  const serviceNameFromParam = searchParams.get("service_name") ?? "";
  const repeatLastRequested = searchParams.get("repeat_last") === "1";
  const dayFromParam = searchParams.get("day");
  const parsedDayFromParam = dayFromParam ? new Date(`${dayFromParam}T00:00:00`) : null;

  const totalSteps = customerIdFromParam ? 4 : 5;
  const stepOffset = customerIdFromParam ? 1 : 0;
  const [step, setStep] = useState<number>(customerIdFromParam ? 2 : 1);

  const [clientSearch, setClientSearch] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(customerIdFromParam);
  const [customerName, setCustomerName] = useState(customerNameFromParam);
  const [branchId, setBranchId] = useState<string | null>(branchIdFromParam);
  const [serviceId, setServiceId] = useState<string | null>(serviceIdFromParam);
  const [serviceName, setServiceName] = useState(serviceNameFromParam);
  const [selectedDay, setSelectedDay] = useState<Date>(parsedDayFromParam ?? new Date());
  const [stripAnchor, setStripAnchor] = useState<Date>(parsedDayFromParam ?? new Date());
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [notes, setNotes] = useState("");
  const [bookError, setBookError] = useState<string | null>(null);

  const customerDetail = useQuery<CustomerItem>({
    queryKey: ["manager-book-customer", customerId],
    queryFn: () => apiGet<CustomerItem>(`/v3/customers/${customerId}`),
    enabled: !!customerId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!customerDetail.data) return;
    if (!customerName) setCustomerName(customerDetail.data.full_name || "");
    if (!branchId && customerDetail.data.branch_id) setBranchId(customerDetail.data.branch_id);
  }, [branchId, customerDetail.data, customerName]);

  const repeatLastQuery = useQuery<{ item: LastAppointmentSummary | null }>({
    queryKey: ["manager-book-repeat-last", customerId],
    queryFn: () => apiGet<{ item: LastAppointmentSummary | null }>(`/v3/customers/${customerId}/last-appointment`),
    enabled: repeatLastRequested && !!customerId,
    staleTime: 30_000,
  });

  useEffect(() => {
    const last = repeatLastQuery.data?.item;
    if (!last) return;
    setBranchId(last.branch_id || branchIdFromParam);
    setServiceId(last.service_id);
    setServiceName(last.service_name || serviceNameFromParam || "");
  }, [branchIdFromParam, repeatLastQuery.data?.item, serviceNameFromParam]);

  const clientsQuery = useQuery<{ items: CustomerItem[] }>({
    queryKey: ["manager-book-clients", clientSearch],
    queryFn: () =>
      apiGet<{ items: CustomerItem[] }>(`/v3/customers?archived=false&size=20${clientSearch ? `&search=${encodeURIComponent(clientSearch)}` : ""}`),
    enabled: step === 1,
  });

  const branchesQuery = useQuery<{ items: Branch[] }>({
    queryKey: ["manager-book-branches"],
    queryFn: () => apiGet<{ items: Branch[] }>("/v3/branches?size=100"),
    staleTime: 60_000,
    enabled: step >= 2,
  });

  const staffQuery = useQuery<{ items: Staff[] }>({
    queryKey: ["manager-book-staff"],
    queryFn: () => apiGet<{ items: Staff[] }>("/v3/staff"),
    staleTime: 60_000,
    enabled: step >= 3,
  });

  const servicesQuery = useQuery<{ items: Service[] }>({
    queryKey: ["manager-book-services", branchId],
    queryFn: () => apiGet<{ items: Service[] }>(`/v3/services?size=100${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ""}`),
    staleTime: 60_000,
    enabled: step >= 3 && !!branchId,
  });

  const dayStr = isoDate(selectedDay);

  const availabilityQuery = useQuery<{ items: Slot[] }>({
    queryKey: ["manager-book-slots", branchId, serviceId, dayStr, customerId],
    queryFn: () =>
      apiGet<{ items: Slot[] }>(
        `/v3/calendar/smart-availability?branch_id=${encodeURIComponent(branchId || "")}&service_id=${encodeURIComponent(
          serviceId || ""
        )}&day=${encodeURIComponent(dayStr)}${customerId ? `&customer_id=${encodeURIComponent(customerId)}` : ""}`
      ),
    enabled: step === 4 && !!branchId && !!serviceId,
  });

  const branches = branchesQuery.data?.items ?? [];
  const staffMap = useMemo(() => {
    const items = staffQuery.data?.items ?? [];
    return new Map(items.map((x) => [x.id, x.full_name]));
  }, [staffQuery.data?.items]);

  useEffect(() => {
    if (step !== 2) return;
    if (branchId && branches.some((b) => b.id === branchId)) {
      setStep(3);
      return;
    }
    if (!branchId && branches.length === 1) {
      setBranchId(branches[0].id);
      setStep(3);
    }
  }, [branchId, branches, step]);

  useEffect(() => {
    if (step !== 3) return;
    const services = servicesQuery.data?.items ?? [];
    if (!services.length) return;
    if (!serviceId) return;
    const matched = services.find((s) => s.id === serviceId);
    if (!matched) {
      setServiceId(null);
      setServiceName("");
      return;
    }
    if (!serviceName) setServiceName(matched.name);
    setStep(4);
  }, [serviceId, serviceName, servicesQuery.data?.items, step]);

  useEffect(() => {
    setSelectedSlot(null);
  }, [serviceId, selectedDay]);

  const strip = useMemo(() => buildDateStrip(stripAnchor), [stripAnchor]);
  const displayStep = step - stepOffset;
  const customerDisplayName = customerDetail.data?.full_name || customerName;

  const createAppointment = useMutation({
    mutationFn: async () => {
      if (!customerId || !branchId || !serviceId || !selectedSlot) {
        throw new Error("Missing booking data");
      }
      return apiPost<AppointmentCreateResult>("/v3/calendar/appointments", {
        branch_id: branchId,
        service_id: serviceId,
        staff_id: selectedSlot.staff_id,
        customer_id: customerId,
        start_at: selectedSlot.start_at,
        source_channel: "crm",
        notes: notes || null,
      });
    },
    onSuccess: async (result) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["manager-calendar-appts"] }),
        qc.invalidateQueries({ queryKey: ["manager-client-conversation", customerId] }),
      ]);

      const params = new URLSearchParams({
        view_from: dayStr === isoDate(new Date()) ? "today" : "day",
        date: dayStr,
      });
      if (branchId) params.set("branch_id", branchId);
      navigate(`/app/calendar/appointment/${result.id}?${params.toString()}`);
    },
    onError: (err) => {
      setBookError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <div className="grid gap-4">
      <section className="manager-ribbon">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-300">Clients</p>
            <h1 className="mt-1 text-2xl font-semibold">Assign Procedure</h1>
            <p className="mt-1 text-sm text-slate-200">
              Create a booking from the client workflow, then jump straight to calendar details.
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn secondary" onClick={() => navigate("/app/clients")}>
              Back to Clients
            </button>
            <button type="button" className="btn secondary" onClick={() => navigate("/app/calendar/today")}>
              Calendar
            </button>
          </div>
        </div>
      </section>

      <section className="panel p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">New booking</p>
            {customerDisplayName ? (
              <p className="text-xs text-slate-500">Client: {customerDisplayName}</p>
            ) : (
              <p className="text-xs text-slate-500">Select client, procedure, and slot</p>
            )}
          </div>
          <span className="text-xs text-slate-500">
            {displayStep}/{totalSteps}
          </span>
        </div>

        <div className="mt-3">
          <StepIndicator step={displayStep} total={totalSteps} />
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn secondary"
              onClick={() => {
                const firstStep = customerIdFromParam ? 2 : 1;
                if (step <= firstStep) {
                  navigate("/app/clients");
                  return;
                }
                setStep(step - 1);
              }}
            >
              Back
            </button>
            {customerDisplayName ? (
              <span className="text-xs rounded-full border border-slate-200 px-3 py-1 text-slate-600">{customerDisplayName}</span>
            ) : null}
          </div>
        </div>

        {repeatLastRequested ? (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {repeatLastQuery.isLoading ? "Loading last procedure..." : null}
            {!repeatLastQuery.isLoading && repeatLastQuery.data?.item ? (
              <>
                Repeating last procedure: <span className="font-semibold">{repeatLastQuery.data.item.service_name || "Service"}</span>{" "}
                ({new Date(repeatLastQuery.data.item.start_at).toLocaleDateString()})
              </>
            ) : null}
            {!repeatLastQuery.isLoading && !repeatLastQuery.data?.item && !repeatLastQuery.error ? "No previous procedure found. Select one manually." : null}
            {repeatLastQuery.error ? <span className="text-red-600">{String(repeatLastQuery.error)}</span> : null}
          </div>
        ) : null}

        {step === 1 ? (
          <div className="mt-4 grid gap-3">
            <label className="text-sm font-medium text-slate-700">Select client</label>
            <input
              className="input"
              type="search"
              placeholder="Search by name, phone, email"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
            />
            {clientsQuery.isLoading ? <p className="text-sm text-slate-500">Searching clients...</p> : null}
            {clientsQuery.error ? <p className="text-sm text-red-600">{String(clientsQuery.error)}</p> : null}
            <div className="grid gap-2">
              {(clientsQuery.data?.items ?? []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="manager-card text-left"
                  onClick={() => {
                    setCustomerId(c.id);
                    setCustomerName(c.full_name);
                    if (c.branch_id) setBranchId(c.branch_id);
                    setStep(2);
                  }}
                >
                  <p className="font-semibold">{c.full_name}</p>
                  <p className="text-sm text-slate-600">{c.phone || c.email || "No contact details"}</p>
                </button>
              ))}
              {!clientsQuery.isLoading && !(clientsQuery.data?.items?.length ?? 0) ? (
                <p className="text-sm text-slate-500">No clients found.</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="mt-4 grid gap-3">
            <label className="text-sm font-medium text-slate-700">Select branch</label>
            {branchesQuery.isLoading ? <p className="text-sm text-slate-500">Loading branches...</p> : null}
            {branchesQuery.error ? <p className="text-sm text-red-600">{String(branchesQuery.error)}</p> : null}
            <div className="grid gap-2">
              {branches.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="manager-card text-left"
                  onClick={() => {
                    setBranchId(b.id);
                    setServiceId(null);
                    setServiceName("");
                    setStep(3);
                  }}
                >
                  <p className="font-semibold">{b.name}</p>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="mt-4 grid gap-3">
            <div className="flex items-center justify-between gap-2">
              <label className="text-sm font-medium text-slate-700">Select procedure</label>
              {branchId ? <span className="text-xs text-slate-500">Branch selected</span> : null}
            </div>
            {servicesQuery.isLoading ? <p className="text-sm text-slate-500">Loading procedures...</p> : null}
            {servicesQuery.error ? <p className="text-sm text-red-600">{String(servicesQuery.error)}</p> : null}
            <div className="grid gap-2">
              {(servicesQuery.data?.items ?? []).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="manager-card text-left"
                  onClick={() => {
                    setServiceId(s.id);
                    setServiceName(s.name);
                    setStep(4);
                  }}
                >
                  <p className="font-semibold">{s.name}</p>
                  <p className="text-xs text-slate-500">{s.duration_minutes} min</p>
                </button>
              ))}
              {!servicesQuery.isLoading && !(servicesQuery.data?.items?.length ?? 0) ? (
                <p className="text-sm text-slate-500">No procedures found for this branch.</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="mt-4 grid gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Pick date and time</p>
              <p className="text-xs text-slate-500 mt-1">{serviceName || "Selected procedure"}</p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn secondary px-3"
                onClick={() =>
                  setStripAnchor((d) => {
                    const next = new Date(d);
                    next.setDate(next.getDate() - 7);
                    return next;
                  })
                }
              >
                Prev
              </button>
              <div className="flex flex-1 gap-2 overflow-x-auto pb-1">
                {strip.map((d) => {
                  const active = isoDate(d) === dayStr;
                  return (
                    <button
                      key={isoDate(d)}
                      type="button"
                      onClick={() => {
                        setSelectedDay(d);
                        setStripAnchor(d);
                      }}
                      className={cn(
                        "min-w-[72px] rounded-xl border px-3 py-2 text-center text-xs",
                        active ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-700"
                      )}
                    >
                      <div>{d.toLocaleDateString([], { weekday: "short" })}</div>
                      <div className="font-semibold">{d.getDate()}</div>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="btn secondary px-3"
                onClick={() =>
                  setStripAnchor((d) => {
                    const next = new Date(d);
                    next.setDate(next.getDate() + 7);
                    return next;
                  })
                }
              >
                Next
              </button>
            </div>

            {availabilityQuery.isLoading ? <p className="text-sm text-slate-500">Loading slots...</p> : null}
            {availabilityQuery.error ? <p className="text-sm text-red-600">{String(availabilityQuery.error)}</p> : null}
            {!availabilityQuery.isLoading && !(availabilityQuery.data?.items?.length ?? 0) ? (
              <p className="text-sm text-slate-500">No available slots for this day.</p>
            ) : null}

            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-4">
              {(availabilityQuery.data?.items ?? []).map((slot) => {
                const active = selectedSlot?.start_at === slot.start_at && selectedSlot?.staff_id === slot.staff_id;
                return (
                  <button
                    key={`${slot.staff_id}:${slot.start_at}`}
                    type="button"
                    onClick={() => {
                      setSelectedSlot(slot);
                      setBookError(null);
                    }}
                    className={cn(
                      "rounded-xl border p-3 text-left",
                      active ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white"
                    )}
                  >
                    <p className="font-semibold text-sm">{formatTime(slot.start_at)}</p>
                    <p className="text-xs text-slate-500 truncate">{staffMap.get(slot.staff_id) || slot.staff_id}</p>
                    <p className="text-[11px] text-amber-700 mt-1">Score {Math.round(slot.score)}</p>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end">
              <button type="button" className="btn" disabled={!selectedSlot} onClick={() => setStep(5)}>
                Continue
              </button>
            </div>
          </div>
        ) : null}

        {step === 5 && selectedSlot ? (
          <div className="mt-4 grid gap-4">
            <h2 className="text-sm font-semibold text-slate-700">Confirm booking</h2>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <div className="grid gap-2">
                <SummaryRow label="Client" value={customerDisplayName || "-"} />
                <SummaryRow label="Procedure" value={serviceName || "-"} />
                <SummaryRow label="Staff" value={staffMap.get(selectedSlot.staff_id) || selectedSlot.staff_id} />
                <SummaryRow
                  label="Date"
                  value={selectedDay.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}
                />
                <SummaryRow label="Time" value={formatTime(selectedSlot.start_at)} />
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-500">Notes (optional)</label>
              <textarea
                className="input mt-1 min-h-[88px]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add an internal or customer-facing note"
              />
            </div>

            {bookError ? <p className="text-sm text-red-600">{bookError}</p> : null}

            <div className="flex justify-end gap-2">
              <button type="button" className="btn secondary" onClick={() => setStep(4)} disabled={createAppointment.isPending}>
                Back to slots
              </button>
              <button type="button" className="btn" onClick={() => createAppointment.mutate()} disabled={createAppointment.isPending}>
                {createAppointment.isPending ? "Creating..." : "Create Booking"}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
